import React from 'react';

import RouterLink from '../../../Router/Link';
import PromotionSlider from '../../../styled/PromotionSlider';
import { formatImage } from '../../../utils/image';

import Box from '../../../styled/Box';
import Button from '../../../styled/Button';
import Container from '../../../styled/Container';
import Distance from '../../../styled/Distance';
import { Heading as VariableHeading } from '../../../styled/VariableHeading/wrappers';
import { Graphic } from '../../../styled/Graphic/wrappers';

export interface IPromotionListSlice {
  backgroundColor: string;
  buttonBackground: string;
  buttonTextColor: string;
  buttonText: string;
  columns: 3 | 4;
  graphic: {
    height: string;
    width: string;
    url: string;
  };
  heading: string;
  headingSizeMobile: number;
  headingSizeDesktop: string;
  headingWeight: FontWeight;
  link: string;
  promotions: {
    image: {
      url: string;
    };
    imageQuality?: number;
    headline: string;
    subline: string;
    subtail: string;
    link: string;
  }[];
  textColor: string;
  titleHeadingLevel?: number;
}

const PromotionListSlice = ({
  backgroundColor,
  buttonBackground,
  buttonTextColor,
  buttonText,
  columns,
  graphic,
  heading,
  headingSizeMobile,
  headingSizeDesktop,
  headingWeight,
  link,
  promotions,
  textColor,
  titleHeadingLevel,
}: IPromotionListSlice) => {
  const imageSizeMd = columns === 3 ? 362 : 268;
  const imageSizeLg = columns === 3 ? 431 : 319;
  const mappedPromotions = promotions.map((p) => {
    const quality = p.imageQuality ? p.imageQuality : undefined;
    return {
      headline: p.headline,
      link: p.link,
      subline: p.subline,
      subtail: p.subtail,
      picture: {
        sources: [
          {
            srcSet: `
            ${formatImage(p.image.url, { width: 254, height: 254, crop: true, output: 'webp', quality })} 254w,
            ${formatImage(p.image.url, { width: 524, height: 524, crop: true, output: 'webp', quality })} 524w,
            ${formatImage(p.image.url, { width: 631, height: 631, crop: true, output: 'webp', quality })} 631w,
            ${formatImage(p.image.url, { width: imageSizeMd, height: imageSizeMd, crop: true, output: 'webp', quality })} ${imageSizeMd}w,
            ${formatImage(p.image.url, { width: imageSizeLg, height: imageSizeLg, crop: true, output: 'webp', quality })} ${imageSizeLg}w,
          `,
            sizes: `(max-width: 375px) 254px, (max-width: 750px) 524px, (max-width: 899px) 631px, (max-width: 1199px) ${imageSizeMd}px, (min-width: 1200px) ${imageSizeLg}px, 631px`,
            type: 'image/webp',
          },
        ],
        image: {
          src: formatImage(p.image.url, { width: 631, height: 631, crop: true, quality }),
          srcSet: `
            ${formatImage(p.image.url, { width: 254, height: 254, crop: true, quality })} 254w,
            ${formatImage(p.image.url, { width: 524, height: 524, crop: true, quality })} 524w,
            ${formatImage(p.image.url, { width: 631, height: 631, crop: true, quality })} 631w,
            ${formatImage(p.image.url, { width: imageSizeMd, height: imageSizeMd, crop: true, quality })} ${imageSizeMd}w,
            ${formatImage(p.image.url, { width: imageSizeLg, height: imageSizeLg, crop: true, quality })} ${imageSizeLg}w,
          `,
          sizes: `(max-width: 375px) 254px, (max-width: 750px) 524px, (max-width: 899px) 631px, (max-width: 1199px) ${imageSizeMd}px, (min-width: 1200px) ${imageSizeLg}px, 631px`,
        },
      } as IResponsivePicture,
    };
  });

  return (
    <div style={{ backgroundColor, color: textColor }}>
      <Container>
        <Box padding="y">
          <Distance size={['2', '6']} />
          {graphic && (
            <div className="text-center">
              <Graphic src={graphic.url} />
            </div>
          )}
          <VariableHeading
            as={titleHeadingLevel === 1 ? 'h1' : undefined}
            className="text-center"
            fontWeight={headingWeight}
            sizeDesktop={headingSizeDesktop}
            sizeMobile={headingSizeMobile}>
            {heading}
          </VariableHeading>
          <Distance size={['3', '4']} />
          <PromotionSlider promotions={mappedPromotions} desktopColumns={columns} />
          {link && (
            <>
              <Distance size={['3', '4']} />
              <div className="l-flex flex-j-c">
                <RouterLink
                  Component={Button}
                  as={'a'}
                  href={link}
                  style={{ backgroundColor: buttonBackground, color: buttonTextColor }}
                  rounded
                  size="small">
                  {buttonText}
                </RouterLink>
              </div>
            </>
          )}
          <Distance size={['2', '6']} />
        </Box>
      </Container>
    </div>
  );
};

export default PromotionListSlice;
